/**
 * Veiviser for import av sanger: velg (sangfiler eller regneark), fyll inn det som mangler, kontroller, importer.
 * Binder stegene til useImport; selve import-API-et er det samme som før (/musikk/import).
 * Se backend/shared/import/README.md.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useImport } from '@/hooks/useImport';
import { lagImportAPI } from '@/services/api/import.api';
import type { ImportAdvarsel, ImportFeilRad } from '@/services/api/types';
import ImportFeilListe from '@/components/common/import/ImportFeilListe';
import { lagMalFil } from '@/components/common/import/lagMalFil';
import { lesSangfiler, sangerTilCsv, type Sang, type SangSvar } from '@/components/common/import/sangfiler/lesSangfiler';
import { harTallakkorder } from '@/lib/sang/tallfil';
import { normaliserToneart } from '@/lib/sang/toneart';
import type { ForhandsvisOpsjoner } from '@/hooks/useImport';
import Steg1Valg from './Steg1Valg';
import Steg2Mangler from './Steg2Mangler';
import Steg3Kontroll, { type MusikkImportRad, type RadRetting } from './Steg3Kontroll';

interface MusikkImportResultat {
  nyeSanger: number;
  hoppetOver: number;
  nyeKategorier: number;
  nyeLenker: number;
  oppdaterteTekster?: number;
}

const MAKS_RADER = 1000;
const MAKS_FILSTORRELSE = 8 * 1024 * 1024;
const MAL_FILNAVN = 'musikkimport.xlsx';

const api = lagImportAPI<MusikkImportRad, MusikkImportResultat>('/musikk/import');

const KOLONNER = [
  { felt: 'tittel', overskrift: 'Tittel' },
  { felt: 'artist', overskrift: 'Artist' },
  { felt: 'album', overskrift: 'Album' },
  { felt: 'utgivelsesaar', overskrift: 'Utgivelsesår' },
  { felt: 'lengde', overskrift: 'Lengde' },
  { felt: 'toneart', overskrift: 'Toneart' },
  { felt: 'bpm', overskrift: 'BPM' },
  { felt: 'takt', overskrift: 'Takt' },
  { felt: 'kategorier', overskrift: 'Kategorier' },
  { felt: 'lenker', overskrift: 'Lenker' },
  { felt: 'tekst_og_akkorder', overskrift: 'Tekst og akkorder' },
  { felt: 'notater', overskrift: 'Notater' },
  { felt: 'publisert', overskrift: 'Publisert' },
];

/** Sanger som finnes fra før står utenfor utvalget. */
const erVelgbar = (rad: { status: string }) => rad.status !== 'finnes';

type Fase = 'valg' | 'mangler';
type Rad = MusikkImportRad;
type RadMelding = { radnr: number; melding: string };

const medKolonne = (f: { kolonne?: string; melding: string }) => (f.kolonne ? `${f.kolonne}: ${f.melding}` : f.melding);

const resultatTekst = (r: MusikkImportResultat): string => {
  const tekst = r.oppdaterteTekster ?? 0;
  return (
    `Importerte ${r.nyeSanger} ${r.nyeSanger === 1 ? 'sang' : 'sanger'}` +
    (tekst > 0 ? `, ${tekst} fikk tekst` : '') +
    (r.hoppetOver > 0 ? `, ${r.hoppetOver} fantes fra før` : '')
  );
};

/** Filadvarsel som ikke hører til noen sang (filen ble hoppet over): filtype, størrelse, kunne ikke leses. */
const utenSang = (advarsler: string[], sanger: Sang[]): string[] =>
  advarsler.filter((a) => !sanger.some((s) => a.startsWith(`${s.filnavn}: `)));

/**
 * Filadvarsler har formen «<filnavn>: <melding>». De som hører til en sang i CSV-en (rad = indeks + 2)
 * knyttes til raden; resten vises over listen.
 */
const fordelFiladvarsler = (
  advarsler: string[],
  sanger: Sang[] | null,
  radnrFinnes: (radnr: number) => boolean
): { perRad: RadMelding[]; generelle: string[] } => {
  const perRad: RadMelding[] = [];
  const generelle: string[] = [];
  for (const a of advarsler) {
    let treff = -1;
    (sanger ?? []).forEach((s, i) => {
      if (a.startsWith(`${s.filnavn}: `) && (treff < 0 || s.filnavn.length > (sanger as Sang[])[treff].filnavn.length)) treff = i;
    });
    const radnr = treff + 2;
    if (treff >= 0 && radnrFinnes(radnr)) perRad.push({ radnr, melding: a.slice((sanger as Sang[])[treff].filnavn.length + 2) });
    else generelle.push(a);
  }
  return { perRad, generelle };
};

interface Props {
  open: boolean;
  onClose: () => void;
  onImportert: () => void;
}

const MusikkImportVeiviser: React.FC<Props> = ({ open, onClose, onImportert }) => {
  const { steg, laster, forhandsvisning, feil, laast, valgte, velgbare, feilRadnr, filadvarsler, velgFil, velgFiler, bekreft, veksleRad, veksleAlle, tilbakestill } =
    useImport<MusikkImportRad, MusikkImportResultat>(api, KOLONNER, {
      maksRader: MAKS_RADER,
      maksFilstorrelse: MAKS_FILSTORRELSE,
      erVelgbar,
      forbigaendeFeilKanProvesIgjen: true,
    });
  const [fase, setFase] = useState<Fase>('valg');
  const [sanger, setSanger] = useState<Sang[] | null>(null);
  const [filer, setFiler] = useState<File[]>([]);
  const [lesAdvarsler, setLesAdvarsler] = useState<string[]>([]);
  const [leser, setLeser] = useState(false);
  const [filfeil, setFilfeil] = useState<string | null>(null);
  const [harSettMangler, setHarSettMangler] = useState(false);
  const [svar, setSvar] = useState<SangSvar>({});
  // Det som faktisk er brukt til å lage forhåndsvisningen: steg 2 pluss rettelser på radene i kontrollen.
  const [brukt, setBrukt] = useState<SangSvar>({});
  // Radene som er rettet siden forrige forhåndsvisning ble sendt.
  const rettede = useRef<Set<number>>(new Set());
  // Utvalget da Importer ble trykket mens rettelser ventet. Importen går når forhåndsvisningen er ferdig.
  const [importVenter, setImportVenter] = useState<ReadonlySet<number> | null>(null);
  const generasjon = useRef(0);
  const malLasterRef = useRef(false);
  const rotRef = useRef<HTMLDivElement>(null);

  const kontroll = (steg === 'forhandsvis' || steg === 'importerer') && forhandsvisning !== null;
  const tomFil = kontroll && forhandsvisning.rader.length === 0 && feil.length === 0;
  const visning = kontroll && !tomFil ? 'kontroll' : fase;
  const importerer = steg === 'importerer';

  const nullstill = () => {
    generasjon.current += 1;
    tilbakestill();
    setFase('valg');
    setSanger(null);
    setFiler([]);
    setLesAdvarsler([]);
    setLeser(false);
    setFilfeil(null);
    setHarSettMangler(false);
    setSvar({});
    setBrukt({});
    rettede.current = new Set();
    setImportVenter(null);
  };

  useEffect(() => {
    if (!open) nullstill();
    // nullstill bruker bare stabile setters og tilbakestill.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fokus til stegtittelen når steget byttes (ikke ved åpning).
  const forrigeVisning = useRef(visning);
  useEffect(() => {
    if (forrigeVisning.current === visning) return;
    forrigeVisning.current = visning;
    rotRef.current?.querySelector<HTMLElement>('[data-steg-tittel]')?.focus();
  }, [visning]);

  const kjor = (liste: Sang[], filene: File[], gjeldende: SangSvar, fraLesing: string[], opsjoner?: ForhandsvisOpsjoner) => {
    setBrukt(gjeldende);
    const csv = sangerTilCsv(liste, gjeldende);
    return velgFiler(filene, async () => ({ csv: csv.csv, filadvarsler: [...fraLesing, ...csv.filadvarsler] }), opsjoner);
  };

  // Rettelsen står i kø til effekten under kan sende den, så ingen rettelse går tapt under en forhåndsvisning.
  const retteRad = (radnr: number, endring: { artist?: string; toneart?: string }) => {
    const sang = sanger?.[radnr - 2];
    if (!sang) return;
    rettede.current.add(radnr);
    setBrukt((forrige) => ({ ...forrige, [sang.filnavn]: { ...forrige[sang.filnavn], ...endring } }));
  };

  const importer = async () => {
    const resultat = await bekreft();
    if (resultat === null) return;
    toast.success(resultatTekst(resultat));
    onImportert();
    onClose();
  };

  // Rettelser sendes samlet når ingen forhåndsvisning pågår. Utvalget beholdes; en rad som ikke var velgbar før,
  // men er det nå, velges. Først når alt er sendt, går en ventende import, og bare hvis ingen valgt rad falt ut.
  useEffect(() => {
    if (laster || !sanger) return;
    // Kontrollen er forlatt: et felt som mister fokus idet det fjernes, skal ikke hente kontrollen tilbake.
    if (!kontroll) {
      rettede.current = new Set();
      return;
    }
    if (rettede.current.size > 0) {
      const rettet = rettede.current;
      rettede.current = new Set();
      const foer = new Set(velgbare);
      void kjor(sanger, filer, brukt, lesAdvarsler, {
        behold: true,
        valg: (nyeVelgbare, forrige) => new Set(nyeVelgbare.filter((nr) => forrige.has(nr) || (rettet.has(nr) && !foer.has(nr)))),
      });
      return;
    }
    if (!importVenter) return;
    setImportVenter(null);
    if (Array.from(importVenter).every((nr) => valgte.has(nr))) void importer();
    // Effekten skal bare reagere på ny rettelse, ferdig forhåndsvisning og ventende import; øvrige verdier leses ferske.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laster, brukt, importVenter]);

  const handleSangfiler = async (valgteFiler: File[]) => {
    if (leser) return;
    const mi = ++generasjon.current;
    setFilfeil(null);
    setSvar({});
    setBrukt({});
    setLeser(true);
    try {
      const lest = await lesSangfiler(valgteFiler, { maksFilstorrelse: MAKS_FILSTORRELSE });
      if (mi !== generasjon.current) return;
      if ('feil' in lest) {
        setFilfeil(lest.feil.map((f) => f.melding).join(' '));
        return;
      }
      setSanger(lest.sanger);
      setFiler(valgteFiler);
      setLesAdvarsler(lest.filadvarsler);
      if (lest.sanger.some((s) => s.mangler.toneart || s.mangler.artist)) {
        setHarSettMangler(true);
        setFase('mangler');
      } else {
        setHarSettMangler(false);
        await kjor(lest.sanger, valgteFiler, {}, lest.filadvarsler);
      }
    } catch {
      if (mi === generasjon.current) setFilfeil('Kunne ikke lese filene. Prøv igjen.');
    } finally {
      if (mi === generasjon.current) setLeser(false);
    }
  };

  const handleRegneark = (fil: File) => {
    setFilfeil(null);
    setSanger(null);
    setHarSettMangler(false);
    void velgFil(fil);
  };

  const handleMal = async () => {
    if (malLasterRef.current) return;
    malLasterRef.current = true;
    try {
      await lagMalFil(await api.hentMal(), MAL_FILNAVN);
    } catch {
      toast.error('Kunne ikke laste ned malen.');
    } finally {
      malLasterRef.current = false;
    }
  };

  const handleImporter = () => {
    if (rettede.current.size > 0) setImportVenter(new Set(valgte));
    else void importer();
  };

  const handleTilbakeFraKontroll = () => {
    tilbakestill();
    setFase(sanger && harSettMangler ? 'mangler' : 'valg');
  };

  const handleTilbakeFraMangler = () => {
    tilbakestill();
    setFase('valg');
  };

  // Rader til kontrollsteget: serverens rader pluss rader som bare finnes som feil.
  const kontrollData = useMemo(() => {
    if (!forhandsvisning) return null;
    const rader: Rad[] = [...forhandsvisning.rader];
    const har = new Set(rader.map((r) => r.radnr));
    for (const f of feil) {
      if (f.radnr === undefined || har.has(f.radnr)) continue;
      har.add(f.radnr);
      const sang = sanger?.[f.radnr - 2];
      rader.push({ radnr: f.radnr, status: 'ny', tittel: sang ? sang.felter.tittel?.trim() || sang.filnavn : `Rad ${f.radnr}` });
    }
    rader.sort((a, b) => a.radnr - b.radnr);
    // Visningen følger brukerens valg (Eb, ikke D#); det som lagres er serverens skrivemåte.
    const visRader = rader.map((r) => {
      const valgt = r.toneart ? brukt[sanger?.[r.radnr - 2]?.filnavn ?? '']?.toneart : undefined;
      return valgt ? { ...r, toneart: valgt } : r;
    });
    const fil = fordelFiladvarsler(filadvarsler, sanger, (nr) => har.has(nr));
    const radFeil: RadMelding[] = [];
    const andreFeil: ImportFeilRad[] = [];
    for (const f of feil) {
      if (f.radnr !== undefined && feilRadnr.has(f.radnr)) radFeil.push({ radnr: f.radnr, melding: medKolonne(f) });
      else andreFeil.push(f);
    }
    const advarsler: ImportAdvarsel[] = [
      ...(forhandsvisning.advarsler ?? []).map((a) => ({ ...a, melding: medKolonne(a) })),
      ...fil.perRad,
    ];
    // Sangfiler kan rettes på raden: artist alltid, toneart på tall-sanger. Teksten endres aldri av valget.
    const rettbare: Record<number, RadRetting> = {};
    (sanger ?? []).forEach((s, i) => {
      if (!har.has(i + 2)) return;
      const r = brukt[s.filnavn];
      const retting: RadRetting = { artist: r?.artist ?? s.felter.artist ?? '' };
      if (harTallakkorder(s.felter.tekst_og_akkorder ?? '')) retting.toneart = r?.toneart?.trim() || normaliserToneart(s.felter.toneart) || '';
      rettbare[i + 2] = retting;
    });
    return { rader: visRader, advarsler, radFeil, andreFeil, generelle: fil.generelle, rettbare };
  }, [forhandsvisning, feil, feilRadnr, filadvarsler, sanger, brukt]);

  const hookFeil = steg === 'velg' ? feil : [];
  const feilTilSteg1 = filfeil ?? (tomFil ? 'Filen har ingen sanger' : null) ?? (hookFeil.length > 0 ? hookFeil.map((f) => f.melding).join(' ') : null);
  const opptatt = leser || laster;
  // Ingen lukking via Escape eller klikk utenfor mens importen pågår.
  const laas = (e: Event) => {
    if (importerer) e.preventDefault();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        aria-describedby={undefined}
        className="max-h-[90vh] max-w-2xl grid-cols-1 overflow-y-auto overflow-x-hidden"
        onEscapeKeyDown={laas}
        onPointerDownOutside={laas}
        onInteractOutside={laas}
      >
        <div ref={rotRef} className="min-w-0">
          {visning === 'valg' && (
            <Steg1Valg
              Tittel={DialogTitle}
              onVelgSangfiler={(f) => void handleSangfiler(f)}
              onVelgRegneark={handleRegneark}
              onLastNedMal={() => void handleMal()}
              feil={feilTilSteg1}
              opptatt={opptatt}
            />
          )}
          {visning === 'mangler' && sanger && (
            <div className="space-y-4">
              <ImportFeilListe feil={hookFeil} />
              {utenSang(lesAdvarsler, sanger).length > 0 && (
                <ul className="space-y-1">
                  {utenSang(lesAdvarsler, sanger).map((a, i) => (
                    <li key={i} role="status" className="text-sm text-status-waiting">
                      {a}
                    </li>
                  ))}
                </ul>
              )}
              <Steg2Mangler
                Tittel={DialogTitle}
                sanger={sanger}
                svar={svar}
                onSvarEndret={setSvar}
                opptatt={laster}
                onFortsett={(svar) => void kjor(sanger, filer, svar, lesAdvarsler)}
                onHoppOver={() => void kjor(sanger, filer, {}, lesAdvarsler)}
                onTilbake={handleTilbakeFraMangler}
              />
            </div>
          )}
          {visning === 'kontroll' && kontrollData && (
            <div className="space-y-4">
              <ImportFeilListe feil={kontrollData.andreFeil} />
              <Steg3Kontroll
                Tittel={DialogTitle}
                stegtekst={harSettMangler ? 'Steg 3 av 3' : 'Steg 2 av 2'}
                rader={kontrollData.rader}
                feilRadnr={feilRadnr}
                rettbare={kontrollData.rettbare}
                onArtistEndret={(radnr, artist) => retteRad(radnr, { artist })}
                onToneartEndret={(radnr, toneart) => retteRad(radnr, { toneart })}
                oppdaterer={laster && !importerer}
                feil={kontrollData.radFeil}
                advarsler={kontrollData.advarsler}
                meldinger={kontrollData.generelle}
                valgte={valgte}
                onVeksleRad={veksleRad}
                onVeksleAlle={veksleAlle}
                onTilbake={handleTilbakeFraKontroll}
                onAvbryt={onClose}
                onImporter={handleImporter}
                artistRedigerbar={!importerer && !importVenter}
                opptatt={importerer || laster}
                stoppet={laast}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MusikkImportVeiviser;
